import { ReactNode } from "react";
import {
  Navigate,
  Route,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router-dom";
import SuccessLottie from "../components/SuccessLottie";
import { getAccessToken, getViewAs } from "../utils/auth-storage";
import ErrorBoundary from "../error-handling/ErrorBoundary";
import RootLayout from "../layout/RootLayout";
import AppLayout from "../layout/app/AppLayout";
import PatientEmailedIntakeForm from "../layout/app/patient-portal/public-view/PatientEmailedIntakeForm";
import PublicLayout from "../layout/app/patient-portal/public-view/PublicLayout";
import PublicPatientAssessment from "../layout/app/patient-portal/public-view/assessments/PublicPatientAssessment";
import PublicDocument from "../layout/app/patient-portal/public-view/document/PublicDocument";
import PublicViewEducationMaterialList from "../layout/app/patient-portal/public-view/education-material/PublicViewEducationMaterialList";
import PublicVideoLayout from "../layout/app/patient-portal/public-view/tele-health/PublicVideoLayout";
import VerifyAndJoinTeleHealth from "../layout/app/patient-portal/public-view/tele-health/VerifyAndJoinTeleHealth";
import AuthLayout from "../layout/auth/AuthLayout";
import LockScreen from "../pages/auth/LockScreen";
// import Login from "../pages/auth/Login";
import ForgotPasswordV2 from "../pages/auth/ForgotPasswordV2";
import LoginV2 from "../pages/auth/LoginV2";
import Logout from "../pages/auth/Logout";
import SetAndResetPassword from "../pages/auth/SetAndResetPassword";
import TwoFactorAuth from "../pages/auth/TwoFactorAuth";
import VerifyEmail from "../pages/auth/VerifyEmail";
import PreviewZoom from "../zoom/Preview";
import DefaultRoutePage from "./DefaultRoutePage";
import { AdminRoute } from "./admin-portal-router/AdminRouter";
import { PatientRoute } from "./patient-portal-router/PatientRouter";
import { ProviderRoute } from "./provider-portal-router/ProviderRouter";
import WidgetRoute from "./widget-router/WidgetRouter";

interface ProtectedRouteProps {
  children: ReactNode;
  accessingViewAs: "admin" | "provider" | "patient";
}

export function ProtectedRoute({
  children,
  accessingViewAs,
}: ProtectedRouteProps) {
  const token = getAccessToken();
  const actualViewAs = getViewAs();

  if (token && accessingViewAs === actualViewAs) {
    return <>{children}</>;
  } else return <Navigate to="/" />;
}

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      path="/"
      element={
        <ErrorBoundary>
          <RootLayout />
        </ErrorBoundary>
      }
    >
      <Route path="public" element={<PublicLayout />}>
        <Route path="document" element={<PublicDocument />} />
        <Route
          path="education-material"
          element={<PublicViewEducationMaterialList />}
        />
        <Route path="questions" element={<PublicPatientAssessment />} />
        <Route path="forms/email" element={<PatientEmailedIntakeForm />} />
        <Route path="submit-success" element={<SuccessLottie />} />

        {/* zoom public route for patient */}
        <Route path="video" element={<PublicVideoLayout />}>
          <Route path="preview" element={<VerifyAndJoinTeleHealth />} />
          <Route path="subs" element={<PreviewZoom isPublicView />} />
        </Route>
      </Route>

      <Route path="auth" element={<AuthLayout />}>
        <Route path="login" element={<LoginV2 />} />
        <Route index path="2FA" element={<TwoFactorAuth />} />
        <Route path="verify-email" element={<VerifyEmail />} />
        <Route
          path="set-password"
          element={<SetAndResetPassword linkType={"set"} />}
        />
        <Route
          path="reset-password"
          element={<SetAndResetPassword linkType={"reset"} />}
        />

        <Route path="forgot-password" element={<ForgotPasswordV2 />} />
        <Route path="logout" element={<Logout />} />
        <Route path="lock-screen" element={<LockScreen />} />
      </Route>
      <Route path="app" element={<AppLayout />}>
        {/* <Route path="join-room" element={<DraggableJoinRoom />} /> */}
        <Route path="preview" element={<PreviewZoom />} />

        {ProviderRoute()}
        {AdminRoute()}
        {WidgetRoute()}
        {PatientRoute()}
      </Route>

      <Route path="*" element={<DefaultRoutePage />} />
    </Route>
  )
);
